/* Grubwick admin — restaurant detail editor with menu-item CRUD. */
import React, { useEffect, useMemo, useState } from "react";
import type {
  VenueType,
  MenuItemRecord,
  LocationRef,
} from "../../../data/adminData";
import { VENUES, CITIES, type Restaurant } from "../../../data/adminData";
import {
  Field,
  Input,
  Select,
  TextArea,
  VenueTag,
  Confirm,
  KebabMenu,
  useToast,
} from "../ui/ui";
import { MenuItemsEditor } from "../MenuItemsEditor/MenuItemsEditor";
import {
  Back,
  Store,
  Tags,
  Trash,
  Info,
  Link,
  Check,
  Spinner,
} from "../icons/icons";
import {
  updateRestaurant,
  deleteRestaurant as apiDeleteRestaurant,
  updateMenuItem,
  deleteMenuItem,
  reorderSections,
  isPersistedId,
} from "../../../api/restaurants";

/* Restaurant-level fields that have a backend column (and so a PATCH is worth
   sending). UI-only fields like venue/hood/style/dish/desc are excluded. */
function restaurantApiFieldsChanged(a: Restaurant, b: Restaurant): boolean {
  return (
    a.name !== b.name ||
    a.street !== b.street ||
    a.city !== b.city ||
    a.state !== b.state ||
    a.zip !== b.zip ||
    a.url !== b.url
  );
}

/* Whether any editable restaurant field changed, ignoring the menu (menu items
   persist instantly on their own). Drives the page's Unsaved/Save state. */
function restaurantDirty(a: Restaurant, b: Restaurant): boolean {
  return JSON.stringify({ ...a, menu: [] }) !== JSON.stringify({ ...b, menu: [] });
}

export function RestaurantEditor({
  source,
  isNew,
  locations = [],
  selectedLocationId = "",
  onSelectLocation,
  menuLoading = false,
  readOnly = false,
  canDelete = true,
  onSave,
  onDelete,
  onBack,
}: {
  source: Restaurant;
  isNew: boolean;
  locations?: LocationRef[];
  selectedLocationId?: string;
  onSelectLocation?: (id: string) => void;
  menuLoading?: boolean;
  /* View-only: the caller can see this restaurant but not edit it (e.g. an owner
     opening a restaurant they don't own). Disables every field and the menu
     editor, and hides the save/delete controls. */
  readOnly?: boolean;
  /* Whether to offer the "Delete restaurant" action — admins only. */
  canDelete?: boolean;
  onSave: (r: Restaurant) => void;
  onDelete: (id: string) => void;
  onBack: () => void;
}): React.ReactElement {
  const [r, setR] = useState<Restaurant>(() =>
    JSON.parse(JSON.stringify(source)),
  );
  const [baseline, setBaseline] = useState(() => JSON.stringify(source));

  // Swap in the freshly-loaded menu when the parent switches location. Keyed on
  // the menu reference, which only changes when the parent re-fetches — the
  // optimistic edits in handleMenuChange don't touch source.menu, so they're
  // preserved, and restaurant-detail edits are left untouched.
  useEffect(() => {
    setR((s) => ({ ...s, menu: source.menu }));
  }, [source.menu]);
  const [confirmDelRestaurant, setConfirmDelRestaurant] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Header slot the menu editor portals its Reorder/Add-item toolbar into.
  const [menuToolbarSlot, setMenuToolbarSlot] = useState<HTMLElement | null>(
    null,
  );
  const toast = useToast();

  // Menu items persist instantly (see handleMenuChange), so the page's
  // dirty/Save state tracks only the restaurant detail fields, not the menu.
  const dirty = restaurantDirty(JSON.parse(baseline), r);
  const set = <K extends keyof Restaurant>(k: K, v: Restaurant[K]) =>
    setR((s) => ({ ...s, [k]: v }));
  const canSave = r.name.trim().length > 0;

  // Items that have no backend id yet — added in-session, can't be persisted
  // (the API has no create endpoint). Surfaced inline so it isn't a surprise.
  const unsyncedItems = r.menu.filter((m) => !isPersistedId(m.id)).length;

  async function save() {
    if (!canSave || saving) return;
    const clean = { ...r, name: r.name.trim() };

    // No create endpoint yet — a brand-new restaurant stays in-memory only.
    if (isNew) {
      onSave(clean);
      setBaseline(JSON.stringify(clean));
      setR(clean);
      toast("Restaurant added — not synced yet");
      return;
    }

    setSaving(true);
    try {
      if (restaurantApiFieldsChanged(JSON.parse(baseline), clean)) {
        await updateRestaurant(clean.id, clean);
      }
      onSave(clean);
      setBaseline(JSON.stringify(clean));
      setR(clean);
      toast("Changes saved");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  // Persist a menu edit the moment it happens. MenuItemsEditor stays a
  // controlled component (so ImportView can reuse it for unsaved extractions);
  // this parent diffs the new list against the old and fires the matching
  // PATCH/DELETE for existing items. The update is optimistic and reverts on
  // failure. Brand-new items (no backend id) can't be persisted yet.
  function handleMenuChange(next: MenuItemRecord[]) {
    const prev = r.menu;
    setR((s) => ({ ...s, menu: next }));
    if (isNew) return; // Nothing to persist against an unsaved restaurant.

    const revert = (msg: string) => {
      setR((s) => ({ ...s, menu: prev }));
      toast(msg);
    };
    const nextIds = new Set(next.map((m) => m.id));
    const prevById = new Map(prev.map((m) => [m.id, m]));

    for (const m of prev) {
      if (isPersistedId(m.id) && !nextIds.has(m.id)) {
        deleteMenuItem(m.id).catch((e) =>
          revert(e instanceof Error ? e.message : "Couldn't delete item"),
        );
      }
    }
    for (const m of next) {
      if (!isPersistedId(m.id)) continue; // new item — no create endpoint
      const was = prevById.get(m.id);
      if (was && JSON.stringify(was) !== JSON.stringify(m)) {
        updateMenuItem(m.id, m).catch((e) =>
          revert(e instanceof Error ? e.message : "Couldn't save item"),
        );
      }
    }
  }

  // Persist a new section order the moment the user saves it in the reorder
  // modal. Optimistic like handleMenuChange: reorder the menu so its sections
  // group in the new order, fire the PUT, and revert on failure. Only wired for
  // a persisted location (passed down from the parent), so locationId is set.
  function handleReorderSections(orderedSectionIds: string[]) {
    if (isNew || !selectedLocationId) return;
    const prev = r.menu;
    const placed = new Set<string>();
    const next: MenuItemRecord[] = [];
    for (const id of orderedSectionIds) {
      for (const m of prev) {
        if (m.sectionId === id) {
          next.push(m);
          placed.add(m.id);
        }
      }
    }
    // Items in no reordered section (the uncategorized bucket) keep their order
    // and trail the reordered sections.
    for (const m of prev) if (!placed.has(m.id)) next.push(m);

    setR((s) => ({ ...s, menu: next }));
    reorderSections(selectedLocationId, orderedSectionIds)
      .then(() => toast("Section order saved"))
      .catch((e) => {
        setR((s) => ({ ...s, menu: prev }));
        toast(e instanceof Error ? e.message : "Couldn't reorder sections");
      });
  }

  async function doDelete() {
    if (deleting) return;
    setDeleting(true);
    try {
      if (isPersistedId(r.id)) {
        await apiDeleteRestaurant(r.id);
      }
      onDelete(r.id); // Navigates away on success; this editor unmounts.
    } catch (e) {
      toast(e instanceof Error ? e.message : "Delete failed");
      setDeleting(false);
      setConfirmDelRestaurant(false);
    }
  }

  // Leaving the editor: prompt (in-app) only when restaurant details are dirty.
  function requestBack() {
    if (dirty) setConfirmDiscard(true);
    else onBack();
  }

  // Discard unsaved restaurant edits but stay on the editor: reset the detail
  // fields back to the baseline (the menu persists on its own, so keep it).
  function discardChanges() {
    const base = JSON.parse(baseline) as Restaurant;
    setR((s) => ({ ...base, menu: s.menu }));
    setConfirmDiscard(false);
  }

  const priceFrom = useMemo(() => {
    const nums = r.menu
      .map((m) => parseFloat(String(m.price).replace(/[^0-9.]/g, "")))
      .filter((n) => !isNaN(n));
    return nums.length ? "$" + Math.min(...nums) : "—";
  }, [r.menu]);

  return (
    <div className="page">
      <button className="backlink" onClick={requestBack}>
        <Back size={15} /> All restaurants
      </button>

      <div className="phead">
        <div>
          <p className="h-eyebrow">
            {isNew ? "New restaurant" : readOnly ? "Viewing" : "Editing"}
          </p>
          <h1 style={{ minHeight: 44 }}>
            {r.name || (isNew ? "Untitled spot" : "")}
          </h1>
          {!isNew && (
            <div style={{ marginTop: 10 }}>
              <VenueTag venue={r.venue} />
            </div>
          )}
        </div>
      </div>

      <div className="editor">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 22,
            minWidth: 0,
          }}
        >
          {/* info panel */}
          <div className="panel">
            <div className="panel-h">
              <h2>
                <span className="ic">
                  <Store size={17} />
                </span>{" "}
                Restaurant details
              </h2>
              {!isNew && canDelete && (
                <KebabMenu
                  items={[
                    {
                      label: "Delete restaurant",
                      icon: <Trash size={15} />,
                      danger: true,
                      onClick: () => setConfirmDelRestaurant(true),
                    },
                  ]}
                />
              )}
            </div>
            <div className="panel-b">
              <div className="fgrid">
                <Field label="Name" span>
                  <Input
                    value={r.name}
                    onChange={(v) => set("name", v)}
                    placeholder="Restaurant name"
                    disabled={readOnly}
                  />
                </Field>
              </div>
              <div className="fgrid two" style={{ marginTop: 16 }}>
                <Field label="Venue type">
                  <Select
                    value={r.venue}
                    onChange={(v) => set("venue", v as VenueType)}
                    options={VENUES}
                    disabled={readOnly}
                  />
                </Field>
                <Field label="Neighborhood">
                  <Input
                    value={r.hood}
                    onChange={(v) => set("hood", v)}
                    placeholder="e.g. North Park"
                    disabled={readOnly}
                  />
                </Field>
                <Field label="Cuisine / style">
                  <Input
                    value={r.style}
                    onChange={(v) => set("style", v)}
                    placeholder="e.g. Korean"
                    disabled={readOnly}
                  />
                </Field>
              </div>
              <div className="fgrid addr" style={{ marginTop: 16 }}>
                <Field label="Street address">
                  <Input
                    value={r.street}
                    onChange={(v) => set("street", v)}
                    placeholder="e.g. 4403 Convoy St"
                    disabled={readOnly}
                  />
                </Field>
                <Field label="City">
                  <Select
                    value={r.city}
                    onChange={(v) => set("city", v)}
                    options={CITIES}
                    disabled={readOnly}
                  />
                </Field>
                <Field label="State">
                  <Input
                    value={r.state}
                    onChange={(v) => set("state", v)}
                    placeholder="CA"
                    disabled={readOnly}
                  />
                </Field>
                <Field label="ZIP">
                  <Input
                    value={r.zip}
                    onChange={(v) => set("zip", v)}
                    placeholder="92111"
                    disabled={readOnly}
                  />
                </Field>
              </div>
              <div className="fgrid" style={{ marginTop: 16 }}>
                <Field
                  label="Signature dish"
                  hint="shown on the search card"
                  span
                >
                  <Input
                    value={r.dish}
                    onChange={(v) => set("dish", v)}
                    placeholder="e.g. Soy-garlic double-fried"
                    disabled={readOnly}
                  />
                </Field>
                <Field label="Website" span>
                  <Input
                    value={r.url}
                    onChange={(v) => set("url", v)}
                    placeholder="https://"
                    disabled={readOnly}
                  />
                </Field>
                <Field label="Description" hint="1–2 sentences" span>
                  <TextArea
                    value={r.desc}
                    onChange={(v) => set("desc", v)}
                    placeholder="A short, appetizing description of the spot."
                    disabled={readOnly}
                  />
                </Field>
              </div>

              {/* save row — commits the restaurant detail fields above
                  (menu items save on their own). Hidden when read-only: a
                  viewer who can't edit has nothing to save. */}
              {readOnly ? (
                <div className="save-bar">
                  <span className="dirty" style={{ color: "var(--muted)" }}>
                    You don’t have permission to edit this restaurant.
                  </span>
                </div>
              ) : (
              <div className="save-bar">
                {dirty ? (
                  <span className="dirty">
                    <span className="dot"></span> Unsaved changes
                  </span>
                ) : (
                  <span className="dirty" style={{ color: "var(--muted)" }}>
                    All changes saved
                  </span>
                )}
                <button className="btn btn-ghost" onClick={requestBack}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={save}
                  disabled={!canSave || !dirty || saving}
                >
                  {saving ? (
                    <span className="spin">
                      <Spinner size={16} />
                    </span>
                  ) : (
                    <Check size={16} />
                  )}{" "}
                  {isNew ? "Create restaurant" : "Save changes"}
                </button>
              </div>
              )}

            </div>
          </div>

          {/* menu panel */}
          <div className="panel">
            <div className="panel-h">
              <h2>
                <span className="ic">
                  <Tags size={17} />
                </span>{" "}
                Menu items{" "}
                <span style={{ color: "var(--muted)", fontWeight: 700 }}>
                  ({r.menu.length})
                </span>
              </h2>
              <div className="panel-h-actions">
                {locations.length > 1 && onSelectLocation && (
                  <select
                    className="sel"
                    value={selectedLocationId}
                    disabled={menuLoading}
                    onChange={(e) => onSelectLocation(e.target.value)}
                    aria-label="Location"
                  >
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name || loc.address || `Location ${loc.id}`}
                      </option>
                    ))}
                  </select>
                )}
                {menuLoading && (
                  <span className="hint spin" style={{ fontWeight: 600 }}>
                    <Spinner size={14} />
                  </span>
                )}
                {/* Menu editor portals its Reorder/Add-item toolbar in here. */}
                <div ref={setMenuToolbarSlot} style={{ display: "contents" }} />
              </div>
            </div>
            <div
              className="panel-b"
              style={{ paddingTop: r.menu.length ? 14 : 4 }}
            >
              {!isNew && unsyncedItems > 0 && (
                <p
                  style={{
                    margin: "0 0 14px",
                    fontSize: 12.5,
                    color: "var(--maroon)",
                    fontWeight: 600,
                  }}
                >
                  {unsyncedItems} new item{unsyncedItems > 1 ? "s" : ""} can’t be
                  saved yet — the API has no endpoint to add menu items. Existing
                  items update instantly.
                </p>
              )}
              <MenuItemsEditor
                items={r.menu}
                onChange={handleMenuChange}
                onReorderSections={
                  !isNew && selectedLocationId && !readOnly
                    ? handleReorderSections
                    : undefined
                }
                toolbarSlot={menuToolbarSlot}
                readOnly={readOnly}
              />
            </div>
          </div>
        </div>

        {/* meta side panel */}
        <div className="panel" style={{ position: "sticky", top: 20 }}>
          <div className="panel-h">
            <h2>
              <span className="ic">
                <Info size={16} />
              </span>{" "}
              Overview
            </h2>
          </div>
          <div className="panel-b">
            <div className="meta-card">
              <div className="stat-row">
                <span className="k">Menu items</span>
                <span className="v">{r.menu.length}</span>
              </div>
              <div className="stat-row">
                <span className="k">Starting price</span>
                <span className="v" style={{ color: "var(--maroon)" }}>
                  {priceFrom}
                </span>
              </div>
              <div className="stat-row">
                <span className="k">Venue</span>
                <span className="v" style={{ fontSize: 12 }}>
                  <VenueTag venue={r.venue} />
                </span>
              </div>
              <div className="stat-row">
                <span className="k">Categories</span>
                <span className="v">
                  {new Set(r.menu.map((m) => m.category)).size}
                </span>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <button
                className="btn btn-ghost btn-sm"
                style={{ width: "100%", justifyContent: "center" }}
                onClick={() => {
                  if (r.url) window.open(r.url, "_blank");
                }}
                disabled={!r.url}
              >
                <Link size={15} /> {r.url ? "Visit website" : "No website set"}
              </button>
            </div>
            <p
              style={{
                fontSize: 11.5,
                color: "var(--muted)",
                margin: "16px 0 0",
                lineHeight: 1.5,
              }}
            >
              {isNew
                ? "New spots aren’t saved to the directory yet — there’s no create endpoint."
                : "Menu item edits save instantly. Restaurant details save when you click Save changes. Brand-new menu items can’t be saved yet (no create endpoint)."}
            </p>
          </div>
        </div>
      </div>

      {confirmDelRestaurant && (
        <Confirm
          title="Delete restaurant?"
          danger
          confirmLabel="Delete"
          requireText="delete"
          busy={deleting}
          autoClose={false}
          body={
            "This removes “" +
            (r.name || "this spot") +
            "” and its " +
            r.menu.length +
            " menu items. This can't be undone."
          }
          onConfirm={doDelete}
          onClose={() => setConfirmDelRestaurant(false)}
        />
      )}

      {confirmDiscard && (
        <Confirm
          title="Discard changes?"
          danger
          confirmLabel="Discard"
          body="This will revert your unsaved changes to the restaurant details. You'll stay on this screen."
          onConfirm={discardChanges}
          onClose={() => setConfirmDiscard(false)}
        />
      )}
    </div>
  );
}
